import React, { useState } from "react";
import { FaRoute } from "react-icons/fa";
import { Header, DateRangePicker } from "../../../components/ui";
import PageLayout from "../../../components/layout/PageLayout";
import api from "../../../services/api";
import { useSocket } from "../../../context/SocketContext";
import RouteCard from "./components/RouteCard";
import RouteModal from "./components/RouteModal";
import RouteActivityModal from "./components/RouteActivityModal";
import AccessModal from "./components/AccessModal"; 
import RouteFilterControls from "./components/RouteFilterControls";
export default function AccessManagement() {
  const [routes, setRoutes] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [accessByRoute, setAccessByRoute] = useState({});
  const [personnelByRoute, setPersonnelByRoute] = useState({});
  const [updatedRoutes, setUpdatedRoutes] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [highlightedRoutes, setHighlightedRoutes] = useState({});
  const HIGHLIGHT_DURATION = 2000; // 2 секунды

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [accessRoute, setAccessRoute] = useState(null);

  const socket = useSocket();


  const [activityRoute, setActivityRoute] = useState(null);

  const openActivityModal = (route) => setActivityRoute(route);
  const closeActivityModal = () => setActivityRoute(null);

  // === Подсветка карточки маршрута ===
  const highlightRoute = (routeId) => {
    setHighlightedRoutes((prev) => ({ ...prev, [routeId]: true }));
    setTimeout(() => {
      setHighlightedRoutes((prev) => ({ ...prev, [routeId]: false }));
    }, HIGHLIGHT_DURATION);
  };

  // === Сокеты ===
  React.useEffect(() => {
    if (!socket) return;

    socket.emit("subscribe:routes");

    const handleNewRoute = (route) => {
      setRoutes((prev) => {
        const exists = prev.find((r) => r.id === route.id);
        if (exists) {
          const updated = prev.map((r) => (r.id === route.id ? route : r));
          highlightRoute(route.id); // подсветка при редактировании
          return updated;
        }

        const updated = [route, ...prev];
        highlightRoute(route.id); // подсветка при создании
        return updated;
      });

      setPersonnelByRoute((prev) => ({
        ...prev,
        [route.id]: {
          driver: route.driver || "",
          loader1: route.loader1 || "",
          loader2: route.loader2 || "",
        },
      }));

      setAccessByRoute((prev) => ({
        ...prev,
        [route.id]: (route.permittedUsers || []).map((u) => u.id),
      }));
    };

    const handleRouteDeleted = (payload) => {
      const id = typeof payload === "object" ? payload.id : payload;
      setRoutes((prev) => prev.filter((r) => r.id !== id));
    };

    socket.on("new_route_created", handleNewRoute);
    socket.on("route_deleted", handleRouteDeleted);

    return () => {
      socket.off("new_route_created", handleNewRoute);
      socket.off("route_deleted", handleRouteDeleted);
      socket.emit("unsubscribe:routes");
    };
  }, [socket]);

  // === Загрузка данных по диапазону ===
  const handleRangeChange = async ({ from, to }) => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const routesData = await api.get("/routes/by-date", {
        params: { startDate: from, endDate: to },
      });
      const users = await api.get("/users");

      setAllUsers(users);
      setRoutes(Array.isArray(routesData) ? routesData : []);

      const initialAccess = {};
      const initialPersonnel = {};
      (routesData || []).forEach((r) => {
        initialAccess[r.id] = (r.permittedUsers || []).map((u) => u.id);
        initialPersonnel[r.id] = {
          driver: r.driver || "",
          loader1: r.loader1 || "",
          loader2: r.loader2 || "",
        };
      });
      setAccessByRoute(initialAccess);
      setPersonnelByRoute(initialPersonnel);
    } catch (err) {
      console.error("Ошибка загрузки маршрутов:", err);
    } finally {
      setLoading(false);
    }
  };

  // === Сортировка маршрутов ===
  const sortedRoutes = React.useMemo(() => {
    return [...routes].sort((a, b) => {
      if (sortBy === "route_number") {
        const numA = Number(a.route_number);
        const numB = Number(b.route_number);

        if (!isNaN(numA) && !isNaN(numB)) {
          return sortOrder === "asc" ? numA - numB : numB - numA;
        }

        // если route_number не число — сортируем как строки
        const strA = a.route_number?.toString() || "";
        const strB = b.route_number?.toString() || "";
        return sortOrder === "asc"
          ? strA.localeCompare(strB, "ru", { numeric: true })
          : strB.localeCompare(strA, "ru", { numeric: true });
      }

      // сортировка по дате создания
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [routes, sortBy, sortOrder]);


  // === Управление доступом ===
  const toggleUserAccess = (routeId, user) => {
    setAccessByRoute((prev) => {
      const copy = { ...prev };
      const arr = copy[routeId] || [];
      copy[routeId] = arr.includes(user.id)
        ? arr.filter((id) => id !== user.id)
        : [...arr, user.id];
      return copy;
    });
  };

// теперь можно передавать вручную нужный список пользователей
const updateAccess = async (routeId, customList) => {
  const updated = customList || accessByRoute[routeId] || [];

  const route = routes.find((r) => r.id === routeId);
  const original = Array.isArray(route?.permittedUsers)
    ? route.permittedUsers.map((u) => u.id)
    : [];

  const toGrant = updated.filter((id) => !original.includes(id));
  const toRevoke = original.filter((id) => !updated.includes(id));

  try {
    await Promise.all([
      ...toGrant.map((userId) =>
        api.post(`/route-access/grant`, { userId, routeListId: routeId })
      ),
      ...toRevoke.map((userId) =>
        api.delete(`/route-access/revoke`, {
          data: { userId, routeListId: routeId },
        })
      ),
    ]);

    setRoutes((prev) =>
      prev.map((r) =>
        r.id === routeId
          ? {
              ...r,
              permittedUsers: allUsers.filter((u) => updated.includes(u.id)),
            }
          : r
      )
    );

    setUpdatedRoutes((p) => ({ ...p, [routeId]: true }));
    setTimeout(() => setUpdatedRoutes((p) => ({ ...p, [routeId]: false })), 1000);
  } catch (err) {
    alert("Не удалось применить изменения доступа");
  }
};



  // === Управление персоналом ===
  const togglePersonnelField = (routeId, field, value) => {
    setPersonnelByRoute((prev) => ({
      ...prev,
      [routeId]: { ...prev[routeId], [field]: value },
    }));
  };

  const savePersonnel = async (routeId) => {
    const { driver, loader1, loader2 } = personnelByRoute[routeId];
    await api.patch(`/routes/${routeId}/personnel`, { driver, loader1, loader2 });
  };

  const handleStatusChange = async (routeId) => {
    await api.patch(`/routes/${routeId}/status`, { route_status: "Открыт" });
    setRoutes((prev) =>
      prev.map((r) => (r.id === routeId ? { ...r, route_status: "Открыт" } : r))
    );
  };

  const openRouteModal = (route) => {
    setSelectedRoute(route);
    setIsRouteModalOpen(true);
  };
  const closeRouteModal = () => {
    setIsRouteModalOpen(false);
    setSelectedRoute(null);
  };

  const routeGroups = routes.reduce((acc, route) => {
    if (!acc[route.route_number]) acc[route.route_number] = [];
    acc[route.route_number].push(route);
    return acc;
  }, {});

  // === Открытие модалки доступа ===
  const openAccessModal = (route) => {
    setAccessRoute(route);
    setIsAccessModalOpen(true);
  };

  const closeAccessModal = () => {
    setAccessRoute(null);
    setIsAccessModalOpen(false);
  };

  return (
    <PageLayout
      header={
        <Header
          icon={FaRoute}
          title="Управление доступом к маршрутам"
          subtitle="Настройка прав доступа и статусов по датам"
        />
      }
      controls={
        <>
          <DateRangePicker mode="range" onChange={handleRangeChange} />
          <RouteFilterControls
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />
        </>
      }

    >
      {loading ? (
        <p>Загрузка данных...</p>
      ) : routes.length === 0 ? (
        <p>Нет маршрутов за выбранный период.</p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
            gap: "0.8rem",
          }}
        >
          {sortedRoutes.map((route) => (
            <RouteCard
              key={route.id}
              route={route}
              highlighted={highlightedRoutes[route.id]}
              allUsers={allUsers}
              accessByRoute={accessByRoute}
              toggleUserAccess={toggleUserAccess}
              updateAccess={updateAccess}
              updatedRoutes={updatedRoutes}
              handleStatusChange={handleStatusChange}
              openRouteModal={openRouteModal}
              personnelByRoute={personnelByRoute}
              togglePersonnelField={togglePersonnelField}
              savePersonnel={savePersonnel}
              editingField={editingField}
              setEditingField={setEditingField}
              routeGroups={routeGroups}
              setRoutes={setRoutes}
              setAccessByRoute={setAccessByRoute}
              openAccessModal={openAccessModal}
              openActivityModal={openActivityModal}
            />
          ))}
        </div>
      )}

      {isRouteModalOpen && selectedRoute && (
        <RouteModal routeId={selectedRoute.id} onClose={closeRouteModal} />
      )}

      {/* === модалка доступа === */}
      {isAccessModalOpen && accessRoute && (
        <AccessModal
          route={accessRoute}
          allUsers={allUsers}
          accessByRoute={accessByRoute}
          setAccessByRoute={setAccessByRoute}   // ← вот это важно!
          updateAccess={updateAccess}
          updatedRoutes={updatedRoutes}
          onClose={closeAccessModal}
        />
      )}
      {activityRoute && (
        <RouteActivityModal route={activityRoute} onClose={closeActivityModal} />
      )}

    </PageLayout>
  );
}
